/**
 * İndirim kampanyaları (M3 WP 3.1/3.4/3.5; keşif S50). Kampanya, taslak satış belgesi kaydedilirken SUNUCUDA
 * değerlendirilir; sonuç satıra elle girilen indirimden AYRI yazılır ve kilitli revizyonda donar.
 *
 * Kurallar (sözleşme docs/spec/castinflow/sales-documents.md §8):
 * 1. Elle girilen satır indirimi (sıfır dahil) her zaman önceliklidir → o satıra kampanya uygulanmaz. Kampanyayı bir satırda
 *    istemeyen kullanıcı satır indirimini 0 girer.
 * 2. Uygun kampanya: etkin; belge tarihi [başlangıç, bitiş] içinde (uçlar dahil, boş = sınırsız); müşteri kriterleri (ilişki
 *    tipi / bölge / etiket — her dolu kriter grubu için en az bir eşleşme) ve ürün kriterleri (kategori / etiket) sağlanır;
 *    satır miktarı en az miktara ulaşır; tutar türünde belge para birimi kampanya para birimine eşittir.
 * 3. Birden çok uygun kampanyada satıra EN BÜYÜK indirim tutarını veren seçilir; eşitlikte önce oluşturulan (sonra kimlik).
 * 4. Yüzde kampanya satıra yüzde indirim olarak; tutar kampanyası BİRİM başına tutar × miktar olarak ve satır brüt tutarıyla
 *    sınırlanarak uygulanır. Yuvarlama fiyat motorunun satır indirimi kuralıyla aynıdır (belge ölçeği + yuvarlama kipi).
 */
import { z } from 'zod';
import { MONEY_POLICY, ZERO, currencySchema, decCmp, decMul, decimalString, parseDecimal, roundTo, scaleOf, toDecimalString, } from './decimal.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
const lookupKey = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/);
export const campaignInputSchema = z
    .object({
    name: z.string().trim().min(1, 'ad gerekli').max(120),
    isActive: z.boolean().default(true),
    validFrom: isoDate.nullable().default(null),
    validTo: isoDate.nullable().default(null),
    discount: z
        .object({
        kind: z.enum(['percent', 'amount']),
        value: decimalString(MONEY_POLICY),
    })
        .strict(),
    /** Tutar türünde zorunlu (birim başına indirim bu para biriminde); yüzde türünde boş */
    currency: currencySchema.nullable().default(null),
    minQuantity: decimalString(MONEY_POLICY).nullable().default(null),
    customerRelationTypes: z.array(lookupKey).max(20).default([]),
    customerRegionKeys: z.array(lookupKey).max(50).default([]),
    customerTagIds: z.array(uuid).max(20).default([]),
    productCategoryIds: z.array(uuid).max(50).default([]),
    productTagIds: z.array(uuid).max(20).default([]),
})
    .strict()
    .superRefine((c, ctx) => {
    if (c.validFrom && c.validTo && c.validTo < c.validFrom)
        ctx.addIssue({
            code: 'custom',
            path: ['validTo'],
            message: 'bitiş başlangıçtan önce olamaz',
        });
    const v = parseDecimal(c.discount.value);
    if (decCmp(v, ZERO) <= 0)
        ctx.addIssue({
            code: 'custom',
            path: ['discount', 'value'],
            message: 'indirim sıfırdan büyük olmalı',
        });
    if (c.discount.kind === 'percent') {
        if (scaleOf(v) > 4)
            ctx.addIssue({
                code: 'custom',
                path: ['discount', 'value'],
                message: 'yüzde en çok 4 ondalık',
            });
        if (decCmp(v, parseDecimal('100')) > 0)
            ctx.addIssue({ code: 'custom', path: ['discount', 'value'], message: 'yüzde en çok 100' });
        if (c.currency !== null)
            ctx.addIssue({
                code: 'custom',
                path: ['currency'],
                message: 'yüzde indirimde para birimi girilmez',
            });
    }
    else if (c.currency === null)
        ctx.addIssue({
            code: 'custom',
            path: ['currency'],
            message: 'tutar indiriminde para birimi gerekli',
        });
});
export const updateCampaignInputSchema = z
    .object({ expectedVersion: z.number().int().min(1), campaign: campaignInputSchema })
    .strict();
export const campaignViewSchema = z.object({
    id: uuid,
    name: z.string(),
    isActive: z.boolean(),
    validFrom: z.string().nullable(),
    validTo: z.string().nullable(),
    discount: z.object({ kind: z.enum(['percent', 'amount']), value: z.string() }),
    currency: z.string().nullable(),
    minQuantity: z.string().nullable(),
    customerRelationTypes: z.array(z.string()),
    customerRegionKeys: z.array(z.string()),
    customerTagIds: z.array(z.string()),
    productCategoryIds: z.array(z.string()),
    productTagIds: z.array(z.string()),
    version: z.number().int(),
    createdAt: z.string(),
});
/** Satırdaki kampanya sonucu (görünüm). */
export const lineCampaignSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: z.enum(['percent', 'amount']),
    value: z.string(),
});
const intersects = (a, b) => {
    if (a.length === 0)
        return true;
    const s = new Set(b.map((x) => x.toLowerCase()));
    return a.some((x) => s.has(x.toLowerCase()));
};
/** Kampanya bu belge/müşteri/satır için uygun mu (kural 2). */
export function campaignApplies(c, ctx, line) {
    if (!c.isActive)
        return false;
    if (c.validFrom !== null && ctx.date < c.validFrom)
        return false;
    if (c.validTo !== null && ctx.date > c.validTo)
        return false;
    if (c.discountKind === 'amount' && c.currency !== ctx.currency)
        return false;
    if (c.minQuantity !== null &&
        decCmp(parseDecimal(line.quantity), parseDecimal(c.minQuantity)) < 0)
        return false;
    const needsCustomer = c.customerRelationTypes.length > 0 ||
        c.customerRegionKeys.length > 0 ||
        c.customerTagIds.length > 0;
    if (needsCustomer) {
        const cu = ctx.customer;
        if (cu === null)
            return false;
        if (!intersects(c.customerRelationTypes, cu.relationTypes))
            return false;
        if (c.customerRegionKeys.length > 0 &&
            (cu.regionKey === null || !c.customerRegionKeys.includes(cu.regionKey)))
            return false;
        if (!intersects(c.customerTagIds, cu.tagIds))
            return false;
    }
    if (c.productCategoryIds.length > 0) {
        if (line.categoryId === null)
            return false;
        if (!intersects(c.productCategoryIds, [line.categoryId]))
            return false;
    }
    if (!intersects(c.productTagIds, line.tagIds))
        return false;
    return true;
}
/** Kural 3–4: satır için en iyi kampanya (yoksa null). Fiyat motoruna verilecek indirim biçimini de döner. */
export function pickCampaign(campaigns, ctx, line) {
    const qty = parseDecimal(line.quantity);
    const gross = decMul(qty, parseDecimal(line.unitPrice));
    if (decCmp(gross, ZERO) <= 0)
        return null;
    let best = null;
    for (const c of campaigns) {
        if (!campaignApplies(c, ctx, line))
            continue;
        let discount;
        let est;
        if (c.discountKind === 'percent') {
            // Karşılaştırma tutarı = brüt × yüzde / 100 (tutar türüyle aynı ölçekte)
            est = decMul(decMul(gross, parseDecimal(c.discountValue)), parseDecimal('0.01'));
            discount = { kind: 'percent', value: c.discountValue };
        }
        else {
            // Birim başına tutar × miktar; satır brütüyle sınırlı; tutar alanı politikası (6 ondalık) için yuvarlanır
            const raw = decMul(qty, parseDecimal(c.discountValue));
            est = roundTo(decCmp(raw, gross) > 0 ? gross : raw, 6, 'half_up');
            discount = { kind: 'amount', value: toDecimalString(est) };
        }
        const cand = { campaign: c, discount, estimated: toDecimalString(est) };
        if (best === null ||
            decCmp(est, parseDecimal(best.estimated)) > 0 ||
            (decCmp(est, parseDecimal(best.estimated)) === 0 &&
                (c.createdAt < best.campaign.createdAt ||
                    (c.createdAt === best.campaign.createdAt && c.id < best.campaign.id))))
            best = cand;
    }
    return best;
}
//# sourceMappingURL=campaigns.js.map